/**
 * Pinia auth store — single source of truth for FE auth state per session-3b
 * briefing §7.2.
 *
 * Status state machine:
 *   - 'unknown'        — initial; bootstrap() not yet resolved.
 *   - 'authenticated'  — /api/auth/me succeeded OR login() succeeded.
 *   - 'guest'          — /api/auth/me returned 401 (no valid session).
 *   - 'expired'        — a /api/* call returned 401 mid-session (set by
 *                        global expiry hook in useApiClient).
 *
 * Cookie is HttpOnly — frontend never reads the cookie directly. State is
 * derived purely from the success/failure pattern of `/api/auth/me`.
 */

import { defineStore } from 'pinia';
import { ApiError, useApiClient } from '@/composables/useApiClient';

export type AuthStatus = 'unknown' | 'authenticated' | 'guest' | 'expired';

export interface AuthState {
  username: string | null;
  expiresAt: string | null;
  status: AuthStatus;
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    username: null,
    expiresAt: null,
    status: 'unknown',
  }),

  actions: {
    /**
     * Submit credentials. On success — set state to authenticated.
     * On 401 / network — re-throw ApiError; LoginView renders inline error.
     *
     * Critical: pass `{ skipExpiredHook: true }` so the login 401 (wrong
     * creds / brute-force lockout) does NOT trigger the global session
     * expired flow.
     */
    async login(username: string, password: string): Promise<void> {
      const api = useApiClient();
      const result = await api.post<{ username: string; expiresAt: string }>(
        '/api/auth/login',
        { username, password },
        { skipExpiredHook: true },
      );
      this.username = result.username;
      this.expiresAt = result.expiresAt;
      this.status = 'authenticated';
    },

    /**
     * POST /api/auth/logout (idempotent 204) and clear local state.
     * Even if the server call fails (e.g., network), we clear local state —
     * the user is leaving anyway, and the cookie is short-lived enough.
     */
    async logout(): Promise<void> {
      const api = useApiClient();
      try {
        await api.post('/api/auth/logout', undefined, {
          skipExpiredHook: true,
        });
      } catch {
        // Tolerated — cookie clear flow continues regardless.
      }
      this.username = null;
      this.expiresAt = null;
      this.status = 'guest';
    },

    /**
     * On app mount / first protected route navigation: probe /api/auth/me.
     *   - 200 → authenticated (set username from response).
     *   - 401 → guest.
     *   - network error → leave status='unknown' (router will retry on next
     *     navigation; UI can render a transient banner).
     */
    async bootstrap(): Promise<void> {
      const api = useApiClient();
      try {
        const me = await api.get<{ username: string }>('/api/auth/me', {
          skipExpiredHook: true,
        });
        this.username = me.username;
        this.status = 'authenticated';
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          this.status = 'guest';
          this.username = null;
          this.expiresAt = null;
          return;
        }
        // Network / non-401 errors — keep status='unknown' so the router can
        // retry on the next navigation (per briefing §7.2). The router guard
        // already redirects to /login when status !== 'authenticated', so the
        // user-facing UX is identical, but a transient network failure no
        // longer locks the user out after backend recovers.
      }
    },

    /**
     * Called by the global 401 hook in useApiClient when any non-login
     * `/api/*` call returns 401 — signal to router that user must re-login.
     */
    markExpired(): void {
      this.status = 'expired';
      this.username = null;
      this.expiresAt = null;
    },

    /** @internal — for tests */
    reset(): void {
      this.username = null;
      this.expiresAt = null;
      this.status = 'unknown';
    },
  },
});
